import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import {
  Upload, FileText, CheckCircle2, AlertCircle,
  Gauge, Calendar, Shield, Handshake, MessageSquare, Sparkles,
} from 'lucide-react';
import { API } from '@/lib/api';
import { httpRequest, HttpError } from '@/lib/http';

const ALLOWED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const DURATIONS = [24, 36, 48, 60];
const KM_PER_YEAR = [10000, 15000, 20000, 25000, 30000];

// Format manuel pour éviter les divergences hydratation : Number.toLocaleString
// peut produire U+202F (narrow no-break space) côté prerender (Puppeteer) et
// U+0020 ou U+00A0 côté client selon les builds Chrome / l'ICU disponible.
function fmt(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export default function NegociationPage() {
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [monthly, setMonthly] = useState('');
  const [duration, setDuration] = useState('');
  const [annualKm, setAnnualKm] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const onDrop = useCallback((accepted, rejected) => {
    if (rejected?.length) {
      const reason = rejected[0]?.errors?.[0]?.code === 'file-too-large'
        ? 'Fichier trop volumineux (max 10 Mo).'
        : 'Type de fichier non supporté. PDF, JPG ou PNG uniquement.';
      setError(reason);
      return;
    }
    if (accepted?.length) {
      setFile(accepted[0]);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_TYPES,
    maxSize: MAX_SIZE_BYTES,
    multiple: false,
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!file) {
      setError('Joins ton devis (PDF, JPG ou PNG).');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('E-mail invalide.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('email', email.trim());
      if (phone.trim()) fd.append('phone', phone.trim());
      if (vehicle.trim()) fd.append('vehicle', vehicle.trim());
      if (monthly.trim()) fd.append('monthly_quote_eur', monthly.trim());
      if (duration) fd.append('duration_months', String(duration));
      if (annualKm) fd.append('annual_km', String(annualKm));
      if (notes.trim()) fd.append('notes', notes.trim());

      const res = await httpRequest(`${API}/quotes/submit-public`, {
        method: 'POST',
        body: fd,
      });
      setResult(res);
      toast.success('Devis envoyé. Je reviens vers toi sous 24h.');
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Envoi impossible. Réessaie dans un instant.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <main className="pt-24 pb-16 max-w-2xl mx-auto px-6 md:px-12">
          <div className="border border-green-500/30 bg-green-950/20 rounded-2xl p-8 md:p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={28} className="text-green-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#f5f5f7] mb-3">Devis bien reçu.</h1>
            <p className="text-[#f5f5f7]/70 leading-relaxed mb-2">
              Je l'analyse, je négocie, je reviens vers toi par e-mail sous 24h
              à <span className="text-[#f5f5f7]">{email}</span>.
            </p>
            <p className="text-xs text-[#f5f5f7]/40 mt-6 font-mono">
              Référence : {result.ticket_id}
            </p>
            <Link to="/" className="inline-block mt-8 text-sm text-[#f5f5f7]/60 hover:text-[#f5f5f7] transition-opacity duration-[180ms]">
              ← Retour à l'accueil
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-12 max-w-3xl mx-auto px-6 md:px-12 text-center">
        <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-green-400 mb-6">
          <Sparkles size={14} /> Négociation avant signature
        </p>
        <h1 className="text-3xl sm:text-5xl md:text-6xl tracking-tighter leading-[1.1] font-black text-[#f5f5f7] mb-6">
          Avant de signer ton leasing,<br />
          <span className="text-green-400">parle-moi 5 minutes.</span>
        </h1>
        <p className="text-lg sm:text-xl text-[#f5f5f7]/70 max-w-2xl mx-auto leading-relaxed">
          Tu as un devis LLD ou LOA chez un concessionnaire. Tu me l'envoies. Je négocie.
          Tu reçois une meilleure offre + des conseils sur les KMs, la durée et l'assurance.
        </p>
      </section>

      {/* Process */}
      <section className="py-12 max-w-5xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: 1, icon: FileText, title: 'Tu reçois ton devis', desc: 'Chez le concessionnaire, avant signature.' },
            { n: 2, icon: MessageSquare, title: 'Tu me l\'envoies', desc: 'Photo ou PDF, via le formulaire ci-dessous.' },
            { n: 3, icon: Handshake, title: 'Je reviens avec mieux', desc: 'Sous 24h. Avec l\'explication et des conseils.' },
          ].map(({ n, icon: Icon, title, desc }) => (
            <div key={n} className="border border-white/8 bg-[#1c1c1e] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-8 rounded-full bg-green-500/15 text-green-400 font-bold flex items-center justify-center text-sm">{n}</span>
                <Icon size={18} className="text-[#f5f5f7]/40" />
              </div>
              <h3 className="text-lg font-bold text-[#f5f5f7] mb-1">{title}</h3>
              <p className="text-sm text-[#f5f5f7]/60 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="py-8 max-w-2xl mx-auto px-6 md:px-12">
        <form onSubmit={handleSubmit} className="border border-white/8 bg-[#1c1c1e] rounded-2xl p-6 md:p-8 space-y-6">
          <h2 className="text-xl font-bold text-[#f5f5f7]">Envoie ton devis</h2>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors duration-[180ms] cursor-pointer ${
              isDragActive ? 'border-green-400 bg-green-500/5' : 'border-white/12 hover:border-white/24'
            }`}
            data-testid="negociation-dropzone"
          >
            <input {...getInputProps({ style: { display: 'none' } })} />
            {file ? (
              <div className="flex items-center justify-center gap-3 text-[#f5f5f7]">
                <FileText size={20} className="text-green-400" />
                <span className="text-sm font-medium truncate max-w-xs">{file.name}</span>
                <span className="text-xs text-[#f5f5f7]/50">({(file.size / 1024 / 1024).toFixed(1)} Mo)</span>
              </div>
            ) : (
              <>
                <Upload size={28} className="mx-auto mb-3 text-[#f5f5f7]/40" />
                <p className="text-sm text-[#f5f5f7]/70 mb-1">Glisse ton devis ou clique pour choisir</p>
                <p className="text-xs text-[#f5f5f7]/40">PDF, JPG ou PNG · 10 Mo max</p>
              </>
            )}
          </div>

          {/* Required */}
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">
              E-mail *
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.fr"
              className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] placeholder:text-[#f5f5f7]/30 focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
            />
          </div>

          {/* Optional grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Téléphone</label>
              <input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] placeholder:text-[#f5f5f7]/30 focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
              />
            </div>

            <div>
              <label htmlFor="vehicle" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Véhicule visé</label>
              <input
                id="vehicle"
                type="text"
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
                placeholder="Ex. Audi A3 Sportback"
                className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] placeholder:text-[#f5f5f7]/30 focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
              />
            </div>

            <div>
              <label htmlFor="monthly" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Mensualité du devis (€)</label>
              <input
                id="monthly"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
                placeholder="499"
                className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] placeholder:text-[#f5f5f7]/30 focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
              />
            </div>

            <div>
              <label htmlFor="duration" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Durée</label>
              <select
                id="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
              >
                <option value="">—</option>
                {DURATIONS.map((d) => <option key={d} value={d}>{`${d} mois`}</option>)}
              </select>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="annualKm" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Kilométrage annuel</label>
              <select
                id="annualKm"
                value={annualKm}
                onChange={(e) => setAnnualKm(e.target.value)}
                className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms]"
              >
                <option value="">—</option>
                {KM_PER_YEAR.map((k) => <option key={k} value={k}>{`${fmt(k)} km/an`}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-xs font-semibold text-[#f5f5f7]/70 mb-2 uppercase tracking-wider">Notes (optionnel)</label>
            <textarea
              id="notes"
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Concession, options choisies, marge de manœuvre…"
              className="w-full bg-[#2c2c2e] border border-white/8 rounded-lg px-4 py-3 text-[#f5f5f7] placeholder:text-[#f5f5f7]/30 focus:outline-none focus:border-green-500/40 focus:ring-4 focus:ring-green-500/20 transition-[border-color,box-shadow] duration-[180ms] resize-none"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="negociation-submit"
            className="w-full btn-green flex items-center justify-center gap-3 px-8 py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Envoi…' : 'Envoyer mon devis'}
          </button>

          <p className="text-xs text-[#f5f5f7]/40 text-center">
            Gratuit. Commission 10 % uniquement sur l'économie obtenue,
            plafonnée à 250 €. Pas d'économie = 0 €.
          </p>
        </form>
      </section>

      {/* Bonus inclus */}
      <section className="py-16 max-w-4xl mx-auto px-6 md:px-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#f5f5f7] mb-2 text-center">
          Inclus, sans surcoût
        </h2>
        <p className="text-[#f5f5f7]/60 text-center mb-10">
          Conseils pour ne pas te faire avoir avant de signer.
        </p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Gauge, label: 'KMs', desc: 'Le bon kilométrage selon ton usage. Sous-estimer = pénalités. Sur-estimer = mensualités gonflées.' },
            { icon: Calendar, label: 'Durée', desc: '24, 36, 48, 60 mois ? La durée optimale dépend de ton profil et de la décote du modèle.' },
            { icon: Shield, label: 'Assurance', desc: 'Quelles options tu dois prendre, lesquelles refuser. La pédagogie est gratuite.' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="text-center">
              <div className="w-14 h-14 rounded-full border-2 border-green-500/40 flex items-center justify-center mx-auto mb-4">
                <Icon size={22} className="text-green-400" />
              </div>
              <h3 className="text-base font-bold text-[#f5f5f7] mb-2 uppercase tracking-wider">{label}</h3>
              <p className="text-sm text-[#f5f5f7]/60 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 max-w-3xl mx-auto px-6 md:px-12 mb-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#f5f5f7] mb-8 text-center">Questions fréquentes</h2>
        <div className="space-y-4">
          {[
            { q: 'Combien de temps prend la négociation ?', a: '24 à 48 heures dans la majorité des cas. Tu reçois un retour par e-mail avec la nouvelle offre et son explication.' },
            { q: 'Avec quels concessionnaires travailles-tu ?', a: 'Toutes marques, neuf et occasion récente. Si la concession refuse de bouger, je te propose des pistes alternatives.' },
            { q: 'Et si tu ne trouves rien de mieux ?', a: 'Tu ne paies rien. La commission de 10 % (plafonnée 250 €) ne s\'applique que sur une économie réellement obtenue.' },
            { q: 'Mes données sont-elles confidentielles ?', a: 'Oui. Le devis n\'est partagé qu\'avec moi pour la négociation. Aucune revente, aucun partage commercial.' },
          ].map(({ q, a }) => (
            <details key={q} className="group border border-white/8 bg-[#1c1c1e] rounded-xl px-5 py-4 open:bg-[#2c2c2e] transition-colors duration-[180ms]">
              <summary className="cursor-pointer text-base font-semibold text-[#f5f5f7] list-none flex items-center justify-between gap-4">
                <span>{q}</span>
                <span className="text-[#f5f5f7]/40 group-open:rotate-45 transition-transform duration-[180ms] text-xl leading-none">+</span>
              </summary>
              <p className="text-sm text-[#f5f5f7]/70 leading-relaxed mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
